// AdminPanelCore.js
import { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";

const API_URL = process.env.REACT_APP_API_URL;

export const useAdminPanel = () => {
  const [files, setFiles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/files`);
      const normalized = res.data.files.map((f) => ({
        id: f.id || f.file_id,
        name: f.name || f.filename,
        uploadedAt: f.created_at,
        tasks: f.tasks || {},
      }));
      setFiles(normalized);
    } catch (err) {
      console.error("Fetching files error:", err);
      pushAlert("Failed to load files", "danger");
    }
  };

  const pushAlert = (msg, type = "success") => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, msg, type }]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, 3000);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("files", file); 
  
    try {
      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
  
      const uploaded = res.data.files?.[0];
  
      if (uploaded) {
        pushAlert("File uploaded successfully", "success");
  
        const normalizedFile = {
          id: uploaded.id || uploaded.file_id,
          name: uploaded.name || uploaded.filename,
          uploadedAt: uploaded.created_at || new Date().toISOString(),
          tasks: uploaded.tasks || {},
        };
  
        setFiles((prev) => [normalizedFile, ...prev]);
      } else {
        pushAlert("Upload failed: No file returned", "danger");
      }
    } catch (err) {
      console.error("Upload error:", err);
      pushAlert("Upload failed: " + (err.response?.data?.message || err.message), "danger");
    }
  };

  const refreshFile = async (fileId) => {
    try {
      const res = await axios.get(`${API_URL}/file/${fileId}`);
      const f = res.data.file;
      return {
        id: f.id || f.file_id,
        name: f.name || f.filename,
        uploadedAt: f.created_at,
        tasks: f.tasks || {},
      };
    } catch (err) {
      console.error("Error refreshing file:", err);
      return null;
    }
  };

  const runAction = async (action, fileObj) => {
    try {
      const res = await axios.post(
        `${API_URL}/file/${fileObj.id}/${action}`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );
  
      if (res.data.success) {
        const inserted = res.data.rowsInserted || 0;
        // pushAlert(`✅ ${action.toUpperCase()} completed: ${inserted} rows inserted`);
        return { success: true, rowsInserted: inserted }; // ✅ return object
      } else {
        // Just in case API returns success: false with a message
        // pushAlert(`Failed to run ${action}: ${res.data.message || 'Unknown error'}`, "danger");
        return { success: false, rowsInserted: 0 };
      }
  
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message;
      console.error(`Error ${action}:`, errMsg);
      pushAlert(`Failed to run ${action}: ${errMsg}`, "danger");
      return { success: false, rowsInserted: 0 };
    }
  };
  

  const runProcess = async (action) => {
    if (!selectedFile) {
      pushAlert("Please select a file", "warning");
      return { rowsInserted: 0 };
    }
  
    const orig = files.find((f) => String(f.id) === String(selectedFile));
    if (!orig) {
      pushAlert("Invalid file selected", "danger");
      return { rowsInserted: 0 };
    }
  
    const file = { ...orig, tasks: { ...orig.tasks } };
  
    // Skip if already completed
    if (file.tasks[action] === "completed") {
      pushAlert(`${action} already completed`, "info");
      return { rowsInserted: 0 };
    }
  
    // If "charge", ensure task and activity are run before it
    // if (action === "charge") {
    //   if (file.tasks.task !== "completed") {
    //     const resultTask = await runAction("task", file);
    //     if (!resultTask?.success) return { rowsInserted: 0 };
    //     const updated = await refreshFile(file.id);
    //     if (updated) file.tasks = updated.tasks;
    //   }
  
    //   if (file.tasks.activity !== "completed") {
    //     const resultActivity = await runAction("activity", file);
    //     if (!resultActivity?.success) return { rowsInserted: 0 };
    //     const updated = await refreshFile(file.id);
    //     if (updated) file.tasks = updated.tasks;
    //   }
    // }
  
    // Run the selected action
    const result = await runAction(action, file);
    if (result?.success) {
      const updated = await refreshFile(file.id);
      if (updated) file.tasks = updated.tasks;
  
      // Update files state
      setFiles((prev) =>
        prev.map((f) => (f.id === file.id ? { ...f, tasks: file.tasks } : f))
      );
  
      return { rowsInserted: result.rowsInserted || 0 };
    } else {
      // pushAlert(`Failed to run ${action}`, "danger");
      // return { rowsInserted: 0 };
    }
  };
  

  return {
    files,
    alerts,
    selectedFile,
    setSelectedFile,
    runProcess,
    uploadFile,      // exposed
    dayjs,
    setFiles,
    pushAlert
    
  };
};
